// Les comptes de l'ÉDITEUR — le trousseau maître.
//
// À NE PAS CONFONDRE avec ComptesClient, qui ouvre l'accès CHEZ un client.
// Ici, chaque ligne voit tous les clients, leurs données, et peut activer
// leurs modules. C'est la liste la plus sensible du logiciel.
//
// POURQUOI CET ÉCRAN EXISTE
//
// Un compte éditeur ne se créait que par `creer_superadmin.py`, en ligne de
// commande sur le serveur. Conséquence : tant qu'un seul compte existait, le
// perdre fermait la plateforme définitivement, et associer quelqu'un demandait
// un accès SSH.
//
// CE QUE L'ÉCRAN DOIT DIRE, ET QUI N'EST PAS DÉCORATIF
//
// Qu'il faut être DEUX. Un utilisateur qui ne voit qu'une ligne ici n'a aucune
// raison de deviner qu'il joue avec le feu : c'est à l'écran de le dire.
import { useCallback, useEffect, useState } from "react";

import api from "../api";
import { proposerMotDePasse } from "./ComptesClient";
import ModaleConfirmation from "./ModaleConfirmation";

const AIDE_MOT_DE_PASSE =
  "12 caractères minimum, sans suite prévisible (1234, abcd), " +
  "sans mot courant, et sans l’adresse e-mail.";

const NOUVEAU_VIDE = { nom: "", prenom: "", email: "", mot_de_passe: "" };

function messageErreur(err, defaut) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return defaut;
}

export default function ComptesEditeur({ user, onFermer }) {
  const [confirmation, setConfirmation] = useState(null);
  const [comptes, setComptes] = useState(null); // null = pas encore chargé
  const [erreur, setErreur] = useState("");
  const [message, setMessage] = useState("");
  const [ouvert, setOuvert] = useState(false);
  const [nouveau, setNouveau] = useState(NOUVEAU_VIDE);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(() => {
    api
      .get("/plateforme/editeurs")
      .then(({ data }) => setComptes(data))
      .catch((err) => setErreur(messageErreur(err, "Chargement impossible.")));
  }, []);

  useEffect(charger, [charger]);

  const creer = (e) => {
    e.preventDefault();
    setEnCours(true);
    setErreur("");
    api
      .post("/plateforme/editeurs", nouveau)
      .then(({ data }) => {
        setMessage(`${data.message} (${data.email})`);
        setOuvert(false);
        setNouveau(NOUVEAU_VIDE);
        charger();
      })
      .catch((err) => setErreur(messageErreur(err, "Création impossible.")))
      .finally(() => setEnCours(false));
  };

  const basculerActif = (compte) => {
    setErreur("");
    setMessage("");

    const basculer = async () => {
      await api.patch(`/plateforme/editeurs/${compte.id}`, { actif: !compte.actif });
      setMessage(
        compte.actif
          ? `${compte.prenom} ${compte.nom} n'a plus accès à la plateforme.`
          : `${compte.prenom} ${compte.nom} a de nouveau accès à la plateforme.`
      );
      charger();
    };

    // Une confirmation ici, et pas ailleurs : couper un accès éditeur est
    // l'action la plus lourde de cet écran, et la liste est courte — donc on
    // clique vite, donc on se trompe de ligne.
    if (!compte.actif) {
      basculer().catch((err) => setErreur(messageErreur(err, "Modification impossible.")));
      return;
    }
    setConfirmation({
      titre: "Couper cet accès éditeur ?",
      sousTitre: `${compte.prenom} ${compte.nom} — ${compte.email}`,
      message: "Ses sessions ouvertes sont coupées immédiatement.",
      libelleConfirmer: "Couper l'accès",
      danger: true,
      onConfirmer: basculer,
    });
  };

  const actifs = (comptes || []).filter((c) => c.actif).length;

  return (
    <>
    <div className="modal-overlay" onClick={onFermer}>
      <div
        className="modal-carte modal-large"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="modal-titre">Comptes éditeur</h3>
        <p className="modal-sous-titre">
          Ces comptes voient <strong>tous les clients</strong> et leurs données.
        </p>

        {message && <p className="note-info">{message}</p>}
        {erreur && <p className="erreur">{erreur}</p>}

        {/* L'avertissement qui justifie l'écran. Affiché seulement quand il
            s'applique : une alerte permanente devient un décor qu'on ne lit
            plus. */}
        {comptes !== null && actifs < 2 && (
          <p className="erreur">
            Il n’y a qu’un seul compte éditeur actif. Si vous perdez son mot de
            passe et son adresse, <strong>la plateforme n’est plus
            administrable</strong> sans intervention sur le serveur. Ouvrez-en
            un second.
          </p>
        )}

        {!ouvert && (
          <div className="modal-actions" style={{ justifyContent: "flex-start" }}>
            <button
              className="btn-accent"
              onClick={() => {
                setNouveau(NOUVEAU_VIDE);
                setOuvert(true);
                setErreur("");
              }}
            >
              + Ouvrir un compte éditeur
            </button>
          </div>
        )}

        {ouvert && (
          <form className="formulaire" onSubmit={creer}>
            <div className="grille-form">
              <label className="champ">
                <span className="champ-label">Prénom *</span>
                <input
                  required
                  value={nouveau.prenom}
                  onChange={(e) =>
                    setNouveau({ ...nouveau, prenom: e.target.value })
                  }
                />
              </label>
              <label className="champ">
                <span className="champ-label">Nom *</span>
                <input
                  required
                  value={nouveau.nom}
                  onChange={(e) => setNouveau({ ...nouveau, nom: e.target.value })}
                />
              </label>
              <label className="champ champ-large">
                <span className="champ-label">E-mail *</span>
                <input
                  required
                  type="email"
                  value={nouveau.email}
                  onChange={(e) =>
                    setNouveau({ ...nouveau, email: e.target.value })
                  }
                />
              </label>
              <label className="champ champ-large">
                <span className="champ-label">Mot de passe *</span>
                <div className="champ-avec-bouton">
                  <input
                    required
                    type="text"
                    value={nouveau.mot_de_passe}
                    onChange={(e) =>
                      setNouveau({ ...nouveau, mot_de_passe: e.target.value })
                    }
                  />
                  <button
                    type="button"
                    className="btn"
                    onClick={() =>
                      setNouveau({
                        ...nouveau,
                        mot_de_passe: proposerMotDePasse(),
                      })
                    }
                  >
                    Proposer
                  </button>
                </div>
              </label>
            </div>
            <p className="sous-info">
              {AIDE_MOT_DE_PASSE} Affiché en clair parce que c’est la{" "}
              <strong>seule fois</strong> où il est lisible : il n’est stocké
              que haché. À transmettre de vive voix.
            </p>
            <div className="actions-form">
              <button
                type="button"
                className="btn"
                onClick={() => setOuvert(false)}
              >
                Annuler
              </button>
              <button className="btn-accent" type="submit" disabled={enCours}>
                {enCours ? "Création…" : "Ouvrir le compte"}
              </button>
            </div>
          </form>
        )}

        {comptes === null ? (
          <p className="note-discrete">Chargement…</p>
        ) : (
          <table className="tableau">
            <thead>
              <tr>
                <th>Nom</th>
                <th>E-mail</th>
                <th>Accès</th>
              </tr>
            </thead>
            <tbody>
              {comptes.map((c) => (
                <tr key={c.id}>
                  <td>
                    {c.prenom} {c.nom}
                    {c.id === user?.id && (
                      <span className="sous-info"> — vous</span>
                    )}
                  </td>
                  <td>{c.email}</td>
                  <td className="actions-cellule">
                    {/* Son propre compte ne se coupe pas : c'est l'erreur de
                        clic la plus banale et la plus définitive. Le serveur
                        la refuse aussi — masquer le bouton ne protège rien,
                        il évite seulement de proposer une impasse. */}
                    {c.id === user?.id ? (
                      <span className="note-discrete">—</span>
                    ) : (
                      <button
                        className={
                          "lien-action" + (c.actif ? " lien-danger" : "")
                        }
                        onClick={() => basculerActif(c)}
                      >
                        {c.actif ? "Couper l’accès" : "Rouvrir l’accès"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="modal-actions">
          <button className="btn" onClick={onFermer}>
            Fermer
          </button>
        </div>
      </div>
    </div>
    {/* APRES la fenetre des comptes, et a cote d'elle plutot que dedans :
        imbriquee, un clic sur le voile de la confirmation traverserait
        jusqu'au voile du dessous et refermerait les deux ecrans d'un coup.
        Placee ensuite, elle s'affiche par-dessus. */}
    {confirmation && (
      <ModaleConfirmation {...confirmation} onFermer={() => setConfirmation(null)} />
    )}
    </>
  );
}
